"use client";

import { useMemo, useCallback, useState } from "react";
import { useDetailContext } from "@/contexts/DetailContextProvider";
import type { ParentEntityType } from "@/contexts/DetailContextProvider.types";
import { convertContextToGraphQLFilter } from "@/lib/utils/crm/contextFilterConverter";

function parentEntityTypeLabel(type: ParentEntityType): string {
  switch (type) {
    case "contact":
      return "Contact";
    case "company":
      return "Company";
    case "deal":
      return "Deal";
    case "ticket":
      return "Ticket";
    case "task":
      return "Task";
    case "time_entry":
      return "Time entry";
    case "offering":
      return "Offering";
    default:
      return "Record";
  }
}

/**
 * Hook to manage context-aware table filtering
 * Automatically applies soft pre-filter based on parent entity context
 *
 * @param nestedEntityType - Type of nested entity being filtered
 * @returns Object containing filter info and control functions
 */
export function useContextualTableFilter(nestedEntityType: string) {
  const { parentEntity, isContextActive } = useDetailContext();
  const [isContextFilterCleared, setIsContextFilterCleared] = useState(false);

  // Compute context filter based on parent entity
  const contextFilter = useMemo(() => {
    if (!isContextActive || isContextFilterCleared) {
      return null;
    }
    return convertContextToGraphQLFilter(parentEntity, nestedEntityType);
  }, [parentEntity, isContextActive, nestedEntityType, isContextFilterCleared]);

  // Function to clear context filter
  const clearContextFilter = useCallback(() => {
    setIsContextFilterCleared(true);
  }, []);

  // Function to restore context filter
  const restoreContextFilter = useCallback(() => {
    setIsContextFilterCleared(false);
  }, []);

  return {
    // Filter information
    contextFilter,
    isContextFilterActive: !!contextFilter && !isContextFilterCleared,
    isContextActive,
    parentEntity,

    // Control functions
    clearContextFilter,
    restoreContextFilter,

    // Display information
    contextFilterLabel:
      isContextActive && parentEntity.name && parentEntity.type
        ? `Filtered by: ${parentEntityTypeLabel(parentEntity.type)}: ${parentEntity.name}`
        : null,
  };
}

/**
 * Hook to get just the context filter without state management
 * Useful for components that only need the filter value
 *
 * @param nestedEntityType - Type of nested entity being filtered
 * @returns Context filter object or null
 */
export function useContextFilter(nestedEntityType: string) {
  const { parentEntity, isContextActive } = useDetailContext();

  return useMemo(() => {
    if (!isContextActive) {
      return null;
    }
    return convertContextToGraphQLFilter(parentEntity, nestedEntityType);
  }, [parentEntity, isContextActive, nestedEntityType]);
}
